import React, { createContext } from 'react';
import { useFeatureVariantAssignmentsQuery } from './graphql/api-platform-graphql';
export const ExperimentationContext = /*#__PURE__*/createContext({
  featureVariantAssignments: new Map(),
  isLoading: false
});
function initializeFeatureVariantAssignments(parentContext, features) {
  const featureVariantAssignments = new Map();

  // Merge with any possible parents
  for (const key of parentContext.featureVariantAssignments.keys()) {
    featureVariantAssignments.set(key, parentContext.featureVariantAssignments.get(key));
  }

  // Mark each feature code requested in the map; this way, we don't log a warning later (see use-features)
  // if the GQL call doesn't have a configuration for a given feature.
  if (features) {
    for (const featureCode of features) {
      // Do not initialize if the parent context already has initialized feature
      if (!featureVariantAssignments.get(featureCode)) {
        featureVariantAssignments.set(featureCode, undefined);
      }
    }
  }
  return featureVariantAssignments;
}
const ExperimentationProviderWithFeatures = ({
  features,
  children
}) => {
  const parentContext = React.useContext(ExperimentationContext);
  // Ensure we do not fetch features that are already being resolved in a parent context
  const featuresToFetch = features.filter(feature => !parentContext.featureVariantAssignments.has(feature));
  const featureVariantAssignments = initializeFeatureVariantAssignments(parentContext, features);
  const {
    isLoading,
    data
  } = useFeatureVariantAssignmentsQuery({
    featureCodes: featuresToFetch
  },
  /**
   * Disable refetching on window focus and reconnect to prevent unnecessary refetches.
   * Source: https://tanstack.com/query/latest/docs/framework/react/reference/useQuery
   */
  {
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity
  });

  // Get feature assignments via GQL
  for (const assignment of (_data$featureVariantA = data === null || data === void 0 ? void 0 : data.featureVariantAssignmentsByFeatureCodes) !== null && _data$featureVariantA !== void 0 ? _data$featureVariantA : []) {
    var _data$featureVariantA;
    featureVariantAssignments.set(assignment.featureCode, assignment);
  }

  // Define context value
  const providerContext = {
    featureVariantAssignments: featureVariantAssignments,
    isLoading: isLoading || parentContext.isLoading
  };

  // Return provider
  return /*#__PURE__*/React.createElement(ExperimentationContext.Provider, {
    value: providerContext
  }, children);
};
const ExperimentationProviderWithNoFeatures = ({
  children
}) => {
  const parentContext = React.useContext(ExperimentationContext);
  const featureVariantAssignments = initializeFeatureVariantAssignments(parentContext);

  // Define context value
  const providerContext = {
    featureVariantAssignments: featureVariantAssignments,
    isLoading: parentContext.isLoading
  };

  // Return provider
  return /*#__PURE__*/React.createElement(ExperimentationContext.Provider, {
    value: providerContext
  }, children);
};
const ExperimentationProviderServerSide = ({
  features,
  children
}) => {
  const parentContext = React.useContext(ExperimentationContext);
  const featureVariantAssignments = initializeFeatureVariantAssignments(parentContext, features);

  // Define context value
  const providerContext = {
    featureVariantAssignments: featureVariantAssignments,
    isLoading: true
  };

  // Return provider
  return /*#__PURE__*/React.createElement(ExperimentationContext.Provider, {
    value: providerContext
  }, children);
};
export const ExperimentationProvider = ({
  features,
  children,
  isServerSide
}) => {
  var _isServerSide;
  // We need an explicit prop for isServerSide because we can't mock undefined window in our testing framework
  // So we'll implement a prop so we can test properly, and use a sane default for checking for a server-side environment
  // istanbul ignore next
  isServerSide = (_isServerSide = isServerSide) !== null && _isServerSide !== void 0 ? _isServerSide : typeof window === 'undefined';
  if (isServerSide) {
    // We shouldn't make GQL experimentation calls from the component render
    // because we don't have reliable user context due to potential static rendering modes.
    // So, we will return provider with a default loading flag state of `true`
    return /*#__PURE__*/React.createElement(ExperimentationProviderServerSide, {
      features: features
    }, children);
  } else if (features.length > 0) {
    // We have features, and we're in a client-side context
    return /*#__PURE__*/React.createElement(ExperimentationProviderWithFeatures, {
      features: features
    }, children);
  } else {
    // We don't have features, and we're in a client-side context
    return /*#__PURE__*/React.createElement(ExperimentationProviderWithNoFeatures, null, children);
  }
};
//# sourceMappingURL=experimentation-context.js.map